"use strict";

const Debug = require('debug');

const {
  Buffer
} = require('buffer');

const {
  keccak256,
  arrayify
} = require('ethers').utils;

const {
  getAddress
} = require('../wallet/address');

const {
  httpRequest
} = require('../utils/api-utils');

const {
  addressSchema,
  stringSchema,
  throwIfMissing,
  positiveIntSchema
} = require('../utils/validator');

const {
  wrapPersonalSign
} = require('../utils/errorWrappers');

const {
  checkSigner
} = require('../utils/utils');

const {
  checkWeb2SecretExists,
  checkRequesterSecretExists
} = require('./check');

const debug = Debug('iexec:sms');
const DOMAIN = 'IEXEC_SMS_DOMAIN';

const concatenateAndHash = function () {
  for (var _len = arguments.length, hexaStringArray = new Array(_len), _key = 0; _key < _len; _key++) {
    hexaStringArray[_key] = arguments[_key];
  }

  const buffer = Buffer.concat(hexaStringArray.map(hexString => Buffer.from(arrayify(hexString))));
  return keccak256(buffer);
};

const getChallengeForSetWeb3Secret = (secretAddress, secretValue) => concatenateAndHash(keccak256(Buffer.from(DOMAIN, 'utf8')), secretAddress, keccak256(Buffer.from(secretValue, 'utf8')));

const getChallengeForSetWeb2Secret = (ownerAddress, secretKey, secretValue) => concatenateAndHash(keccak256(Buffer.from(DOMAIN, 'utf8')), ownerAddress, keccak256(Buffer.from(secretKey, 'utf8')), keccak256(Buffer.from(secretValue, 'utf8')));

const handleNonUpdatablePushSecret = _ref => {
  let {
    response,
    signerAddress,
    targetAddress
  } = _ref;

  if (response.ok) {
    return true;
  }

  if (response.status === 409) {
    throw Error("Secret already exists for ".concat(targetAddress, " and can't be updated"));
  }

  if (response.status === 401) {
    throw Error("Wallet ".concat(signerAddress, " is not allowed to set secret for ").concat(targetAddress));
  }

  throw Error("SMS answered with unexpected status: ".concat(response.status, " ").concat(response.statusText));
};

const pushWeb3Secret = async function () {
  let contracts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : throwIfMissing();
  let smsURL = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : throwIfMissing();
  let resourceAddress = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : throwIfMissing();
  let secretValue = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : throwIfMissing();

  try {
    checkSigner(contracts);
    const vResourceAddress = await addressSchema({
      ethProvider: contracts.provider
    }).validate(resourceAddress);
    const vSignerAddress = await getAddress(contracts);
    await stringSchema().validate(secretValue, {
      strict: true
    });
    const challenge = getChallengeForSetWeb3Secret(vResourceAddress, secretValue);
    const binaryChallenge = arrayify(challenge);
    const auth = await wrapPersonalSign(contracts.signer.signMessage(binaryChallenge));
    const res = await httpRequest('POST')({
      api: smsURL,
      endpoint: '/secrets/web3',
      query: {
        secretAddress: vResourceAddress
      },
      body: secretValue,
      headers: {
        Authorization: auth
      }
    }).catch(e => {
      debug(e);
      throw Error("SMS at ".concat(smsURL, " didn't answered"));
    });
    return handleNonUpdatablePushSecret({
      response: res,
      signerAddress: vSignerAddress,
      targetAddress: vResourceAddress
    });
  } catch (error) {
    debug('pushWeb3Secret()', error);
    throw error;
  }
};

const pushWeb2Secret = async function () {
  let contracts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : throwIfMissing();
  let smsURL = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : throwIfMissing();
  let secretName = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : throwIfMissing();
  let secretValue = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : throwIfMissing();
  let {
    forceUpdate = false
  } = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};

  try {
    checkSigner(contracts);
    const ownerAddress = await getAddress(contracts);
    await stringSchema().validate(secretName, {
      strict: true
    });
    await stringSchema().validate(secretValue, {
      strict: true
    });
    const secretExists = await checkWeb2SecretExists(contracts, smsURL, ownerAddress, secretName);

    if (secretExists && !forceUpdate) {
      throw Error("Secret \"".concat(secretName, "\" already exists for ").concat(ownerAddress));
    }

    const update = !!secretExists;
    const challenge = getChallengeForSetWeb2Secret(ownerAddress, secretName, secretValue);
    const binaryChallenge = arrayify(challenge);
    const auth = await wrapPersonalSign(contracts.signer.signMessage(binaryChallenge));
    const res = await httpRequest(update ? 'PUT' : 'POST')({
      api: smsURL,
      endpoint: '/secrets/web2',
      query: {
        ownerAddress,
        secretName
      },
      body: secretValue,
      headers: {
        Authorization: auth
      }
    }).catch(e => {
      debug(e);
      throw Error("SMS at ".concat(smsURL, " didn't answered"));
    });

    if (res.ok) {
      return {
        isPushed: true,
        isUpdated: update
      };
    }

    throw Error("SMS answered with unexpected status: ".concat(res.status, " ").concat(res.statusText));
  } catch (error) {
    debug('pushWeb2Secret()', error);
    throw error;
  }
};

const pushRequesterSecret = async function () {
  let contracts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : throwIfMissing();
  let smsURL = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : throwIfMissing();
  let secretName = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : throwIfMissing();
  let secretValue = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : throwIfMissing();

  try {
    checkSigner(contracts);
    const requesterAddress = await getAddress(contracts);
    await stringSchema().validate(secretName, {
      strict: true
    });
    await stringSchema().validate(secretValue, {
      strict: true
    });
    const secretExists = await checkRequesterSecretExists(contracts, smsURL, requesterAddress, secretName);

    if (secretExists) {
      throw Error("Secret \"".concat(secretName, "\" already exists for ").concat(requesterAddress));
    }

    const challenge = getChallengeForSetWeb2Secret(requesterAddress, secretName, secretValue);
    const binaryChallenge = arrayify(challenge);
    const auth = await wrapPersonalSign(contracts.signer.signMessage(binaryChallenge));
    const res = await httpRequest('POST')({
      api: smsURL,
      endpoint: "/requesters/".concat(requesterAddress, "/secrets/").concat(secretName),
      body: secretValue,
      headers: {
        Authorization: auth
      }
    }).catch(e => {
      debug(e);
      throw Error("SMS at ".concat(smsURL, " didn't answered"));
    });

    if (res.ok) {
      return {
        isPushed: true
      };
    }

    throw Error("SMS answered with unexpected status: ".concat(res.status, " ").concat(res.statusText));
  } catch (error) {
    debug('pushRequesterSecret()', error);
    throw error;
  }
};

const pushAppSecret = async function () {
  let contracts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : throwIfMissing();
  let smsURL = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : throwIfMissing();
  let appAddress = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : throwIfMissing();
  let secretValue = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : throwIfMissing();
  let secretIndex = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 1;

  try {
    checkSigner(contracts);
    const vSignerAddress = await getAddress(contracts);
    const vAppAddress = await addressSchema({
      ethProvider: contracts.provider
    }).validate(appAddress);
    const vSecretIndex = await positiveIntSchema().validate(secretIndex);
    await stringSchema().validate(secretValue, {
      strict: true
    });
    const challenge = getChallengeForSetWeb2Secret(vAppAddress, vSecretIndex.toString(), secretValue);
    const binaryChallenge = arrayify(challenge);
    const auth = await wrapPersonalSign(contracts.signer.signMessage(binaryChallenge));
    const res = await httpRequest('POST')({
      api: smsURL,
      endpoint: "/apps/".concat(vAppAddress, "/secrets/").concat(vSecretIndex),
      body: secretValue,
      headers: {
        Authorization: auth
      }
    }).catch(e => {
      debug(e);
      throw Error("SMS at ".concat(smsURL, " didn't answered"));
    });
    return handleNonUpdatablePushSecret({
      response: res,
      signerAddress: vSignerAddress,
      targetAddress: vAppAddress
    });
  } catch (error) {
    debug('pushAppSecret()', error);
    throw error;
  }
};

module.exports = {
  pushWeb2Secret,
  pushWeb3Secret,
  pushRequesterSecret,
  pushAppSecret
};